// The two orthogonal axes of a client record.
//
//   service_type  — who authorizes and pays (the payer/program)
//   service_lines — what we deliver
//
// They are separate because they vary independently: a GAPP child may also
// receive DME supplies, and a DME-only client may be Medicaid or private pay.
// Collapsing them into one field makes the combination unrepresentable, which
// is what forced every DME client to claim GAPP before this existed.
//
// GAPP-specific behaviour (supervisory cadence, DCH intake packets, DMA-6A /
// DMA-80 authorization fields) must gate on the payer, never on the service
// line. Anything DME-specific gates on the service line, never on the payer.

export const PAYERS = [
  {
    key: 'gapp',
    label: 'GAPP',
    short: 'GAPP',
    desc: 'Georgia Pediatric Program — Medicaid-funded skilled nursing & personal care for children under 21',
  },
  {
    key: 'medicaid',
    label: 'Medicaid',
    short: 'Medicaid',
    desc: 'Fee-for-service Medicaid outside GAPP — typically DME & supplies only',
  },
  {
    key: 'caresource',
    label: 'CareSource',
    short: 'CareSource',
    desc: 'Medicaid CMO — claims go to CareSource, never to GAMMIS. The only payer for DME.',
  },
  {
    key: 'private_insurance',
    label: 'Private Insurance / CMO',
    short: 'Insurance',
    desc: 'A commercial plan or another Medicaid CMO (Amerigroup, Peach State) — claims go to the plan',
  },
  {
    key: 'self_pay',
    label: 'Self-Pay',
    short: 'Self-Pay',
    desc: 'Invoiced directly to the family',
  },
];

export const SERVICE_LINES = [
  { key: 'home_health', label: 'Home Health', desc: 'Skilled nursing and/or personal support in the home' },
  { key: 'dme',         label: 'DME / Supplies', desc: 'Incontinence, ostomy, catheter and other supplies' },
];

/**
 * Coerce a loose payer value to one of the three canonical keys.
 *
 * leads.service_type is free text — the website contact form writes whatever
 * the marketing site sends ("GAPP Pediatric Services", "General Inquiry",
 * "Private In-Home Care"). clients.service_type is constrained, so anything
 * promoted from a lead has to be normalized or the insert is rejected.
 * Unrecognized values fall back to 'gapp', which is where they landed before
 * this function existed (the column default).
 */
export const normalizePayer = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return 'gapp';
  if (PAYERS.some(p => p.key === v)) return v;
  if (v.includes('gapp') || v.includes('pediatric')) return 'gapp';
  if (v.includes('self-pay') || v.includes('self pay') || v.includes('selfpay')) return 'self_pay';
  if (v.includes('caresource') || v.includes('care source')) return 'caresource';
  if (v.includes('cmo') || v.includes('insurance')) return 'private_insurance';
  // Legacy 'private' and loose "private care" wording predate the split.
  // Without an insurance signal, self-pay is the safer reading.
  if (v.includes('private')) return 'self_pay';
  if (v.includes('medicaid')) return 'medicaid';
  return 'gapp';
};

/** Best-effort service lines from a free-text service description. */
export const normalizeServiceLines = (value, fallback = ['home_health']) => {
  if (Array.isArray(value) && value.length) {
    const kept = value.filter(l => SERVICE_LINES.some(sl => sl.key === l));
    if (kept.length) return kept;
  }
  const v = String(value || '').toLowerCase();
  const wantsDme = /\bdme\b|supplies|equipment|incontinence|ostomy|catheter/.test(v);
  return wantsDme ? ['dme'] : fallback;
};

export const payerLabel  = (t) => PAYERS.find(p => p.key === t)?.label || '—';
export const payerShort  = (t) => PAYERS.find(p => p.key === t)?.short || '—';

// ── Where a claim files ─────────────────────────────────────────────────────
//
// A billing run is built the same way for every payer — hours, codes, units,
// authorization — and differs only in where it goes. GAPP and fee-for-service
// Medicaid both file with GAMMIS over SFTP. CareSource is filed on
// CareSource's side and the claim number recorded here; nothing CareSource
// ever reaches the transport. That guard lives in filesWithGammis(), and the
// run's `payer` column is what it reads.
export const RUN_DESTINATIONS = [
  { key: 'gammis',     label: 'GAMMIS',     desc: 'GAPP and fee-for-service Medicaid — filed over SFTP' },
  { key: 'caresource', label: 'CareSource', desc: 'Filed with CareSource outside CAERA; claim number recorded here' },
];

/** Payer keys whose claims go to GAMMIS. Everything else is filed elsewhere. */
export const GAMMIS_PAYERS = ['gapp', 'medicaid'];

/** The payer every DME claim goes to. AHFC's DME line bills CareSource only. */
export const DME_PAYER = 'caresource';

/**
 * The run destination for a client payer key. An unknown or missing key
 * (an AxisCare visit whose client is not matched) is read as GAMMIS, which
 * is where every run went before this existed.
 */
export const destinationForPayer = (payerKey) =>
  payerKey === 'caresource' ? 'caresource' : 'gammis';

/** True for a run that may be sent to GAMMIS. The one check the transport trusts. */
export const filesWithGammis = (run) => !run?.payer || run.payer === 'gammis';

export const destinationLabel = (key) => RUN_DESTINATIONS.find(d => d.key === key)?.label || 'GAMMIS';

const linesOf = (client) => (Array.isArray(client?.service_lines) ? client.service_lines : []);

/** Authorized and paid through GAPP. Gate GAPP paperwork and cadence on this. */
export const isGapp = (client) => client?.service_type === 'gapp';

/**
 * The private home-care track: in-home care that is NOT GAPP, regardless of
 * who pays for it. Governed by HFRD Rule 111-8-65 rather than the GAPP
 * manual — private_intake packet, 92/122-day supervisory cadence, intake
 * assessment inside the packet. Never test a specific non-GAPP key for this;
 * self-pay, private insurance and non-GAPP Medicaid all belong here.
 */
export const isPrivateTrack = (client) => !!client?.service_type && !isGapp(client);

/** Receives supplies. May also receive nursing. */
export const hasDme = (client) => linesOf(client).includes('dme');

/** Receives care in the home — i.e. someone is delivering care to supervise. */
export const hasHomeHealth = (client) => linesOf(client).includes('home_health');

/**
 * Supplies only, no one in the home. These clients carry no supervisory
 * obligation, get no GAPP or private-home-care intake packet, and are not
 * subject to the DMA-6A / DMA-80 authorization fields.
 */
export const isDmeOnly = (client) => hasDme(client) && !hasHomeHealth(client);

/** The full client-status vocabulary, in pipeline order. */
export const CLIENT_STATUSES = [
  'New Referral', 'Client Contacted', 'Response Pending', 'Waitlist',
  'Intake Form Submitted', 'Nursing Assessment Pending', 'Nursing Assessment Completed',
  'PPOT Request Pending', 'PPOT Request In Progress',
  'Submitted (DMA6A)', 'Submitted (DMA80)', 'Approved by GAPP',
  'Active (Receiving Service)', 'On-Hold/Suspended', 'Service Suspended',
  'Discharged', 'GAPP Application Denied', 'Duplicate', 'Lost',
];

/**
 * Closed statuses: the client is not receiving service and is not heading
 * towards it. Mirrors zone 'Inactive' in the status_options table, which is
 * what the database (clients_derive_zone, clients_on_close) acts on.
 *
 * A closed client is out of every monitoring surface — supervision, nursing
 * assessment, PA expiry, care plans, CMN, DME auth, the daily digest — and
 * appears only in the dashboard's total count and on its own profile.
 * Closing one pauses (On-Hold/Suspended, Service Suspended) or ends (the
 * rest) its schedules and cancels its future visits; tasks are left alone.
 */
export const CLOSED_STATUSES = [
  'On-Hold/Suspended', 'Service Suspended',
  'Discharged', 'GAPP Application Denied', 'Duplicate', 'Lost',
];

/**
 * Receiving (or cleared to receive) care: the clients scheduling, the MAR
 * board and the calendar's authorization view work with.
 */
/** Receiving care now. Spell it with this, never by hand. */
export const RECEIVING_SERVICE = 'Active (Receiving Service)';
/** DCH has approved; care is about to start. */
export const APPROVED_BY_GAPP = 'Approved by GAPP';

export const IN_SERVICE_STATUSES = [RECEIVING_SERVICE, APPROVED_BY_GAPP];

/** On hold rather than gone: schedules pause instead of ending. */
export const PAUSED_STATUSES = ['On-Hold/Suspended', 'Service Suspended'];

const statusOf = (clientOrStatus) =>
  typeof clientOrStatus === 'string' ? clientOrStatus : clientOrStatus?.current_status;

/** Receiving care right now. Accepts a client row or a bare status string. */
export const isReceivingService = (clientOrStatus) => statusOf(clientOrStatus) === RECEIVING_SERVICE;

/** Receiving care, or cleared to. Accepts a client row or a bare status string. */
export const isInService = (clientOrStatus) => IN_SERVICE_STATUSES.includes(statusOf(clientOrStatus));

/** Accepts a client row or a bare status string. */
export const isClosedClient = (clientOrStatus) => CLOSED_STATUSES.includes(statusOf(clientOrStatus));

/** PostgREST filter value for `.not('current_status', 'in', CLOSED_STATUS_FILTER)`. */
export const CLOSED_STATUS_FILTER = `(${CLOSED_STATUSES.map(s => `"${s}"`).join(',')})`;

/**
 * Client-status values that only make sense on the GAPP authorization path.
 * Non-GAPP clients move New Referral → Client Contacted → Active; their
 * authorization is tracked per-order in dme_orders, not in this status.
 */
export const GAPP_ONLY_STATUSES = [
  'Nursing Assessment Pending',
  'Nursing Assessment Completed',
  'PPOT Request Pending',
  'PPOT Request In Progress',
  'Submitted (DMA6A)',
  'Submitted (DMA80)',
  'Approved by GAPP',
  'GAPP Application Denied',
];

/**
 * Statuses selectable for a payer. `current` is always included even when it is
 * GAPP-only: a client switched off the GAPP track keeps the status they already
 * have until someone changes it deliberately, rather than having the select
 * silently land on a different value.
 */
export const statusesForPayer = (serviceType, current) => {
  const allowed = serviceType === 'gapp'
    ? CLIENT_STATUSES
    : CLIENT_STATUSES.filter(s => !GAPP_ONLY_STATUSES.includes(s));
  return current && !allowed.includes(current)
    ? CLIENT_STATUSES.filter(s => allowed.includes(s) || s === current)
    : allowed;
};
